import {useCallback, useEffect, useRef, useState} from 'react'
import {
  ActivityIndicator,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native'
import {
  type RecordingOptions,
  RecordingPresets,
  requestRecordingPermissionsAsync,
  useAudioRecorder,
  useAudioRecorderState,
} from 'expo-audio'
import * as DocumentPicker from 'expo-document-picker'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'

import {
  beginRecordingSession,
  endRecordingSession,
} from '#/lib/flashbookAudioSession'
import {clipDurability, rememberLocalClip} from '#/lib/flashbookClips'
import {
  armRecorderTap,
  disarmRecorderTap,
  forgetTappedStream,
} from '#/lib/flashbookMicTap'
import {useAgent} from '#/state/session'
import * as Toast from '#/view/com/util/Toast'
import {
  AUDIO_EDITOR_AVAILABLE,
  FlashBookAudioEditor,
} from '#/components/flashbooks/FlashBookAudioEditor'
import {FlashBookReadAloud} from '#/components/flashbooks/FlashBookReadAloud'
import {FlashBookRecordingCard} from '#/components/flashbooks/FlashBookRecordingCard'
import {uploadClip} from '#/components/flashbooks/flashbookSave'
import {type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'

type Props = {
  /** The block's recorded reading audio, if it has one. */
  url?: string
  accent: string
  /** Persist a change — `undefined` clears the block's audio. */
  onChange: (url: string | undefined) => void
  /** Overrides "READING AUDIO" — the cover calls its clip an intro. */
  label?: string
}

/** The microphone is only usable where the platform actually has one. */
function canRecord(): boolean {
  if (Platform.OS !== 'web') return true
  return (
    typeof navigator !== 'undefined' &&
    typeof (globalThis as {MediaRecorder?: unknown}).MediaRecorder !==
      'undefined'
  )
}

/**
 * The containers the browser will record in, best first.
 *
 * M4A — AAC in MP4 — is the one container every platform this app runs on can
 * both *play* and *edit*: iOS records it natively, Chrome decodes it, and
 * WebKit's decoder, which refuses Opus in WebM outright, takes it. WebM is the
 * browser recorder's own default, and it is why a take recorded in Chrome could
 * be neither heard nor polished on an iPhone.
 */
const RECORDING_MIME_TYPES = [
  'audio/mp4',
  'audio/webm;codecs=opus',
  'audio/webm',
]

/**
 * The recorder's settings.
 *
 * The container is *inside* the `web` half, and that is the whole point of this
 * block. `createRecordingOptions` (in `expo-audio`) builds what the platform
 * actually receives as `{...common, ...options.web}` — so a `mimeType` set beside
 * `ios` and `android` is spread away by the preset's own `web.mimeType`, and the
 * recorder went on producing WebM while this file said, in a comment, that it was
 * producing M4A.
 *
 * That was not a cosmetic difference. **WebKit cannot play Opus in WebM**, so a
 * take recorded in the browser could not be heard on an iPhone (or in Safari on
 * the machine that recorded it), and the editor could not decode it to polish it
 * either: the creator recorded a reading and the play button did nothing. M4A —
 * AAC in MP4 — is the one container every platform this app runs on can both play
 * and edit, which is why it is asked for first.
 *
 * Metering is asked for on every platform: it is the only source of a *live* level
 * on a device, and it is what makes the recording screen answer the creator's
 * voice rather than merely count the seconds.
 */
export const RECORDING_OPTIONS: RecordingOptions = (() => {
  const base = RecordingPresets.HIGH_QUALITY
  if (Platform.OS !== 'web' || typeof MediaRecorder === 'undefined') {
    return {...base, isMeteringEnabled: true} as RecordingOptions
  }
  let mimeType: string | undefined
  try {
    mimeType = RECORDING_MIME_TYPES.find(mime =>
      MediaRecorder.isTypeSupported(mime),
    )
  } catch {
    mimeType = undefined
  }
  return {
    ...base,
    isMeteringEnabled: true,
    web: {...base.web, ...(mimeType ? {mimeType} : {})},
  } as RecordingOptions
})()

/**
 * The reading-audio field of a block.
 *
 * Three ways to give a block a voice, in the order a creator actually reaches
 * for them: record it here, upload a file, or polish a take in the audio editor.
 * Whichever they choose, the result is a *player* — transport, progress and
 * clock — rather than a button that only says "Lecture": the creator can hear
 * exactly what the learner will hear, and check the pacing before publishing.
 *
 * Editing is deliberately non-destructive to the creator's confidence: the take
 * they recorded is remembered, so an edit can always be auditioned *against* it
 * and reverted, rather than silently overwriting their performance.
 *
 * The other half of that promise is where the clip *lives*. A recording arrives
 * as bytes only this tab can resolve — `blob:` on the web, `file:` on a phone —
 * and a book left holding one is a book that stays silent for everyone else and
 * forgets its own narration on the next reload. So the field uploads the clip the
 * moment it appears and hands the block the asset URL instead, keeping the local
 * bytes in the session cache (`#/lib/flashbookClips`) so the desk can still open
 * a take it just recorded without downloading it back.
 *
 * Two consequences worth stating, because they are the design:
 *  • it is silent when it succeeds — the creator asked for a reading, not for a
 *    progress bar, and the block simply holds a real URL from then on;
 *  • it says so when it fails, and the save pipeline still uploads local clips,
 *    so a take made offline is kept rather than lost.
 */
export function FlashBookAudioField({url, accent, onChange, label}: Props) {
  const styles = useStyles()
  const chrome = useStudioChrome()
  const {t: l} = useLingui()
  const recorder = useAudioRecorder(RECORDING_OPTIONS)
  const recState = useAudioRecorderState(recorder, 200)
  /**
   * The recorder's own live level, in dBFS.
   *
   * A *reader* rather than a number, and stable across renders by design: the
   * meter samples it sixteen times a second, and a new function each render would
   * tear down and re-open the measuring graph on every keystroke elsewhere in the
   * studio. On the web the recorder reports no metering at all — the browser
   * measures the borrowed stream's samples itself — so this returns nothing there
   * and costs one call.
   */
  const readRecorderDbfs = useCallback(
    () => recorder.getStatus().metering ?? null,
    [recorder],
  )
  const [picking, setPicking] = useState(false)
  const [asking, setAsking] = useState(false)
  const [recording, setRecording] = useState(false)
  /** The take is held, not ended: the recorder is still open and keeps its bytes. */
  const [paused, setPaused] = useState(false)
  const [editing, setEditing] = useState(false)
  /** The untouched take behind a finished edit, so it can be reverted. */
  const [originalUrl, setOriginalUrl] = useState<string | undefined>(undefined)
  const agent = useAgent()
  const did = agent.session?.did
  /**
   * Whether this clip has reached the creator's own server yet.
   *
   * `idle` is the state that lies least: it is what an asset looks like, what a
   * signed-out creator looks like, and what a successful upload decays back to.
   */
  const [upload, setUpload] = useState<{state: 'idle' | 'saving' | 'failed'}>({
    state: 'idle',
  })
  /** Bumped by "Send again", so a failed upload can be retried by hand. */
  const [uploadAttempt, setUploadAttempt] = useState(0)

  const recordable = canRecord()
  /**
   * `onChange` is a fresh closure on every studio render, so it is reached
   * through a ref: the upload effect below must not restart — and re-upload the
   * same clip — because the parent re-rendered.
   */
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // ── A clip becomes an asset as soon as it exists ────────────────────────────
  useEffect(() => {
    /*
     * Nothing to do for a clip that is already an asset, and nothing we *can* do
     * without a session: a signed-out creator (or the dev fixture, which has no
     * session by construction) keeps the clip on this device and the save pipeline
     * uploads it. Claiming anything here would be a lie about work being safe.
     */
    const plan = clipDurability(url, did)
    if (plan.kind !== 'upload') {
      setUpload(previous =>
        previous.state === 'idle' ? previous : {state: 'idle'},
      )
      return
    }
    let cancelled = false
    setUpload({state: 'saving'})
    void uploadClip({agent, did: plan.did, uri: plan.url})
      .then(asset => {
        if (cancelled) return
        // The bytes stay reachable locally: editing the take you just recorded
        // must not require downloading the take you just recorded.
        rememberLocalClip(asset, plan.url)
        setUpload({state: 'idle'})
        onChangeRef.current(asset)
      })
      .catch(() => {
        if (!cancelled) setUpload({state: 'failed'})
      })
    return () => {
      cancelled = true
    }
  }, [agent, did, uploadAttempt, url])

  const pickAudio = useCallback(async () => {
    setPicking(true)
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['audio/*'],
        copyToCacheDirectory: true,
      })
      if (!result.canceled && result.assets[0]) {
        setOriginalUrl(undefined)
        onChange(result.assets[0].uri)
      }
    } catch {
      Toast.show(l`Could not open that file.`, 'circle-exclamation')
    } finally {
      setPicking(false)
    }
  }, [l, onChange])

  const startRecording = useCallback(async () => {
    setAsking(true)
    try {
      const permission = await requestRecordingPermissionsAsync()
      if (!permission.granted) {
        Toast.show(
          l`Allow microphone access to record the reading yourself.`,
          'circle-exclamation',
        )
        return
      }
      /*
       * The session first, and on a device it is not optional: iOS will not let a
       * recorder capture at all until the platform has been told the session is a
       * recording one. See `flashbookAudioSession` — the same call, undone below,
       * is also what puts playback back where the creator expects it afterwards.
       */
      await beginRecordingSession()
      /*
       * The meter's one chance at a level, and it has to come first.
       *
       * `prepareToRecordAsync` is the call that opens the microphone, and the
       * recording screen's meter reads *that* capture — borrowed for the beat it
       * exists (see `flashbookMicTap`). Armed after the recorder, there would be
       * no stream to borrow and the screen would fall back to a clock on every
       * browser, which is the state Safari was stuck in.
       */
      armRecorderTap()
      try {
        await recorder.prepareToRecordAsync()
      } finally {
        // One shot either way: a wrapper left in place would keep watching for a
        // microphone the recorder has already taken.
        disarmRecorderTap()
      }
      recorder.record()
      setPaused(false)
      setRecording(true)
    } catch {
      await endRecordingSession()
      Toast.show(
        l`Recording is not available on this device.`,
        'circle-exclamation',
      )
    } finally {
      setAsking(false)
    }
  }, [l, recorder])

  const finishRecording = useCallback(
    async (keep: boolean) => {
      try {
        await recorder.stop()
        const uri = recorder.uri
        setRecording(false)
        setPaused(false)
        if (keep && uri) {
          setOriginalUrl(undefined)
          onChange(uri)
        }
      } catch {
        setRecording(false)
        setPaused(false)
      } finally {
        // Hand the session back whichever way the take ended: a kept take, a
        // discarded one, and a failed one all mean "stop being a microphone".
        await endRecordingSession()
        // And forget the borrowed stream: its tracks have been stopped by the
        // recorder, and a meter must never hold — or be handed back — a capture
        // that belongs to a take that is over.
        forgetTappedStream()
      }
    },
    [onChange, recorder],
  )

  /** Hold the take where it is, or carry on from there. */
  const togglePause = useCallback(() => {
    try {
      if (paused) {
        recorder.record()
        setPaused(false)
      } else {
        recorder.pause()
        setPaused(true)
      }
    } catch {
      // A device that will not pause a take is a device where the take simply
      // runs on: nothing to tell the creator, and nothing lost.
    }
  }, [paused, recorder])

  /**
   * Whether a take is running, readable from a cleanup without re-running it.
   *
   * This is load-bearing rather than tidiness. The teardown below must fire on
   * *unmount* and on nothing else — and an effect that names `recording` fires its
   * cleanup every time that flag flips, which is exactly when a take is starting.
   * So `setRecording(true)` was running the cleanup `forgetTappedStream()` in the
   * commit *before* the recording card's own effect could sample the borrowed
   * stream, and the meter came up reading nothing in every browser: the screen
   * answered "We cannot hear anything yet" while the creator was talking into a
   * live microphone. The flag is read through a ref instead, so the teardown keeps
   * its one job.
   */
  const recordingRef = useRef(false)
  recordingRef.current = recording

  // A recording must never outlive the editor it belongs to — and neither must
  // the session it borrowed.
  useEffect(() => {
    return () => {
      if (recordingRef.current) void recorder.stop().catch(() => {})
      void endRecordingSession()
      disarmRecorderTap()
      forgetTappedStream()
    }
    // Deliberately only the recorder: see `recordingRef` above.
  }, [recorder])

  return (
    <View style={styles.wrap}>
      <View style={styles.labelRow}>
        <Feather name="volume-2" size={12} color={chrome.textMuted} />
        <Text style={styles.label}>{label ?? l`READING AUDIO`}</Text>
        <Text style={styles.labelHint}>{l`optional`}</Text>
      </View>

      {/*
        Polish a take without leaving the block: while the desk is open it
        replaces the row of actions, so there is one thing to look at.

        *Where* it opens is a platform decision, not a style one. The desk renders
        where the block's audio band was — under three text fields, inside a card
        that scrolls — so on the web it scrolls itself into view, and on a phone
        it arrives as a sheet instead. Opening in place there looked like nothing
        had happened: the creator was left looking at the card they had just been
        looking at.
      */}
      {editing && !!url ? (
        Platform.OS === 'web' ? (
          <FlashBookAudioEditor
            url={url}
            accent={accent}
            onApply={next => {
              setOriginalUrl(url)
              onChange(next)
              setEditing(false)
            }}
            onClose={() => setEditing(false)}
          />
        ) : (
          <Modal
            visible
            transparent
            animationType="slide"
            onRequestClose={() => setEditing(false)}>
            <View style={[styles.sheetScrim, {backgroundColor: chrome.scrim}]}>
              <View
                style={[
                  styles.sheet,
                  {backgroundColor: chrome.fill, borderColor: chrome.border},
                ]}>
                <View style={styles.sheetHandle} />
                <ScrollView
                  style={styles.sheetScroll}
                  contentContainerStyle={styles.sheetContent}
                  keyboardShouldPersistTaps="handled">
                  <FlashBookAudioEditor
                    url={url}
                    accent={accent}
                    onApply={next => {
                      setOriginalUrl(url)
                      onChange(next)
                      setEditing(false)
                    }}
                    onClose={() => setEditing(false)}
                  />
                </ScrollView>
              </View>
            </View>
          </Modal>
        )
      ) : (
        <>
          {/* The player: whoever supplied the audio, this is what the learner gets. */}
          {!!url && !recording && (
            <FlashBookReadAloud
              source={{url}}
              accent={accent}
              mutedColor={chrome.textMuted}
              dense
            />
          )}

          {!!url && originalUrl && originalUrl !== url && !recording && (
            <View style={styles.editedRow}>
              <Feather name="sliders" size={11} color={accent} />
              <Text style={[styles.editedTxt, {color: accent}]}>
                {l`Effects applied`}
              </Text>
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel={l`Undo the effects`}
                accessibilityHint={l`Puts back the recording you made`}
                onPress={() => {
                  const restore = originalUrl
                  setOriginalUrl(undefined)
                  onChange(restore)
                }}
                style={styles.miniGhost}>
                <Feather name="rotate-ccw" size={11} color={chrome.textMuted} />
                <Text style={styles.miniGhostTxt}>{l`Undo`}</Text>
              </TouchableOpacity>
            </View>
          )}

          {recording ? (
            /*
             * The take owns the screen while it runs: a card that is *listening*
             * to the creator, not a row of buttons that happens to include a red
             * dot. Metering is asked of the recorder — the device's own level —
             * which is why the reader is passed rather than the level itself.
             */
            <FlashBookRecordingCard
              elapsedMs={recState?.durationMillis ?? 0}
              accent={accent}
              paused={paused}
              readDbfs={readRecorderDbfs}
              onTogglePause={togglePause}
              onUse={() => void finishRecording(true)}
              onDiscard={() => void finishRecording(false)}
            />
          ) : (
            <View style={styles.btnRow}>
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel={url ? l`Replace audio` : l`Upload audio`}
                accessibilityHint={l`Picks an audio file for this block`}
                onPress={() => void pickAudio()}
                disabled={picking}
                style={[styles.btn, {borderColor: accent + '55'}]}>
                {picking ? (
                  <ActivityIndicator size="small" color={accent} />
                ) : (
                  <Feather name="music" size={13} color={accent} />
                )}
                <Text style={[styles.btnTxt, {color: accent}]}>
                  {url ? l`Replace` : l`Upload audio`}
                </Text>
              </TouchableOpacity>

              {recordable && (
                <TouchableOpacity
                  accessibilityRole="button"
                  accessibilityLabel={l`Record reading`}
                  accessibilityHint={l`Records your voice with the microphone`}
                  onPress={() => void startRecording()}
                  disabled={asking}
                  style={[styles.btn, {borderColor: chrome.danger + '66'}]}>
                  {asking ? (
                    <ActivityIndicator size="small" color={chrome.danger} />
                  ) : (
                    <Feather name="mic" size={13} color={chrome.danger} />
                  )}
                  <Text
                    style={[
                      styles.btnTxt,
                      {color: chrome.danger},
                    ]}>{l`Record`}</Text>
                </TouchableOpacity>
              )}

              {!!url && (
                <TouchableOpacity
                  accessibilityRole="button"
                  accessibilityLabel={l`Remove audio`}
                  accessibilityHint={l`Removes the audio from this block`}
                  onPress={() => {
                    setOriginalUrl(undefined)
                    onChange(undefined)
                  }}
                  style={[styles.btn, {borderColor: chrome.dangerBorder}]}>
                  <Feather name="trash-2" size={13} color={chrome.danger} />
                  <Text style={[styles.btnTxt, {color: chrome.danger}]}>
                    {l`Remove`}
                  </Text>
                </TouchableOpacity>
              )}

              {/* Only offered once there is a take to work on, and only where the
              platform can actually run the effect engine. */}
              {!!url && !recording && AUDIO_EDITOR_AVAILABLE && (
                <TouchableOpacity
                  accessibilityRole="button"
                  accessibilityLabel={l`Edit audio`}
                  accessibilityHint={l`Opens the voice effects and trim desk`}
                  onPress={() => setEditing(true)}
                  style={[styles.btn, {borderColor: accent + '55'}]}>
                  <Feather name="sliders" size={13} color={accent} />
                  <Text
                    style={[styles.btnTxt, {color: accent}]}>{l`Edit`}</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </>
      )}

      {/*
        Where the clip lives, said plainly — and only when there is something to
        say. A successful upload says nothing: the block now holds an asset, and a
        tick that confirms the expected outcome is noise on every card.

        A failure has to speak, because the difference is real: the take plays
        here and nowhere else until it is sent. The reassurance in the sentence is
        not a sop — the save pipeline uploads local clips, so the work is kept
        either way; what is at stake is only *when* it travels.
      */}
      {upload.state === 'saving' && (
        <View style={styles.uploadRow} testID="flashbook-clip-upload">
          <ActivityIndicator size="small" color={chrome.textFaint} />
          <Text style={styles.uploadTxt}>
            {l`Saving so this reading follows you to your other devices…`}
          </Text>
        </View>
      )}
      {upload.state === 'failed' && (
        <View
          style={[
            styles.uploadRow,
            {
              borderColor: chrome.warningBorder,
              backgroundColor: chrome.warningTint,
            },
          ]}
          testID="flashbook-clip-upload">
          <Feather name="cloud-off" size={12} color={chrome.warning} />
          <Text style={styles.uploadTxt}>
            {l`On this device only for now — the reading is sent when you save the book.`}
          </Text>
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={l`Send the reading again`}
            accessibilityHint={l`Uploads this recording so it plays on every device`}
            onPress={() => setUploadAttempt(n => n + 1)}
            hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}
            style={styles.miniGhost}>
            <Text style={[styles.uploadRetry, {color: chrome.warning}]}>
              {l`Send again`}
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  )
}

const makeStyles = (chrome: StudioChrome) =>
  StyleSheet.create({
    wrap: {gap: 10},
    labelRow: {flexDirection: 'row', alignItems: 'center', gap: 6},
    label: {
      fontSize: 10,
      fontWeight: '800',
      letterSpacing: 1.2,
      color: chrome.textMuted,
    },
    labelHint: {
      fontSize: 10,
      fontWeight: '600',
      color: chrome.textFaint,
    },
    btnRow: {flexDirection: 'row', gap: 8, flexWrap: 'wrap'},
    btn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      borderWidth: 1,
      borderRadius: 20,
      paddingHorizontal: 12,
      paddingVertical: 7,
    },
    btnTxt: {fontSize: 12.5, fontWeight: '700'},
    sheetScrim: {flex: 1, justifyContent: 'flex-end'},
    sheet: {
      borderTopLeftRadius: 22,
      borderTopRightRadius: 22,
      borderWidth: 1,
      borderBottomWidth: 0,
      paddingTop: 8,
      // Room for the home indicator, so the last dial is never under it.
      paddingBottom: 24,
      maxHeight: '88%',
    },
    sheetHandle: {
      alignSelf: 'center',
      width: 38,
      height: 4,
      borderRadius: 2,
      backgroundColor: chrome.borderStrong,
      marginBottom: 6,
    },
    sheetScroll: {flexGrow: 0},
    sheetContent: {paddingHorizontal: 12, paddingBottom: 4},
    uploadRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 7,
      borderWidth: 1,
      borderColor: 'transparent',
      borderRadius: 12,
      paddingHorizontal: 9,
      paddingVertical: 7,
    },
    uploadTxt: {
      flex: 1,
      fontSize: 11,
      lineHeight: 15,
      color: chrome.textFaint,
    },
    uploadRetry: {fontSize: 11, fontWeight: '800'},
    editedRow: {flexDirection: 'row', alignItems: 'center', gap: 7},
    editedTxt: {flex: 1, fontSize: 11, fontWeight: '700', letterSpacing: 0.2},
    miniGhost: {flexDirection: 'row', alignItems: 'center', gap: 4, padding: 4},
    miniGhostTxt: {fontSize: 11, fontWeight: '700', color: chrome.textMuted},
  })

/** The field's styles for the appearance the app is in. */
const useStyles = () => useStudioStyles(makeStyles)
